import { fetchApi } from './api'
import { BACKSTOP_PAGE_SIZE } from '../types/requestBackstop'
import type {
  BackstopAssigneeList, BackstopRequest, BackstopRequestDetail, BackstopRequestList, RequestBackstopSummary,
} from '../types/requestBackstop'

/**
 * The request backstop (`ObservatoryRequestsBackstopController`, `api/observatory/requests`; Cartographer doc 15 §7) —
 * Observatory's one act surface over Cartographer data (D6a, amended narrowly by Q-2b = a).
 *
 * The READS are LighthouseAdmin at class level on MultiAuth, like every other Cartographer-section read, so they go
 * through the shared `fetchApi` and a failure arrives as an ApiError carrying its status: the page tells a 403 (the
 * role) from a 503 (the request table is not provisioned) from a 500.
 *
 * The INTERVENTIONS — force close, reassign, reroute, a corrected due date, and platform access's forward, attest and
 * refuse — are LighthouseAdmin AND pinned to JwtBearer on the server (EtlWriteAuthorization, A4), exactly as clearing a
 * halt is: the shared API key and Windows sign-ins get a 401 in every environment. So each is sent
 * `auth: 'bearer-only'`: with no Entra token it throws BearerRequiredError BEFORE sending (errorKind
 * 'sign-in-required') and never falls back to the key. There is no approve here, and no refusal of content access: those
 * belong to the people who own the content, and the server has no such route.
 */

const BASE = '/api/observatory/requests'

/** How many requests are on the backstop, and why. 200 with `storeProvisioned: false` before the migration, never a 503. */
export function getRequestBackstopSummary(): Promise<RequestBackstopSummary> {
  return fetchApi<RequestBackstopSummary>(`${BASE}/summary`)
}

/** One page of the backstop, longest there first. The defaults (skip 0, top 50) are left out of the query string. */
export function getBackstopRequests(query: { skip?: number; top?: number } = {}): Promise<BackstopRequestList> {
  const params = new URLSearchParams()
  if (query.skip != null && query.skip !== 0) params.set('skip', String(query.skip))
  if (query.top != null && query.top !== BACKSTOP_PAGE_SIZE) params.set('top', String(query.top))
  const qs = params.toString()
  return fetchApi<BackstopRequestList>(`${BASE}${qs ? `?${qs}` : ''}`)
}

/** One request, on the backstop or not, with what may be done to it now and its audit trail. The id is ONE path segment. */
export function getBackstopRequest(id: string): Promise<BackstopRequestDetail> {
  return fetchApi<BackstopRequestDetail>(`${BASE}/${encodeURIComponent(id)}`)
}

/** Who a commission may be reassigned to: the live members of its workspace. Empty for any other kind. */
export function getBackstopAssignees(id: string): Promise<BackstopAssigneeList> {
  return fetchApi<BackstopAssigneeList>(`${BASE}/${encodeURIComponent(id)}/assignees`)
}

// ---- interventions (bearer-only) -----------------------------------------------------------------------------

/** Every intervention, by the route segment the server serves it at. */
export type BackstopAction =
  | { action: 'force-close'; id: string; reason: string }
  | { action: 'refuse'; id: string; reason: string }
  | { action: 'forward'; id: string; note?: string | null }
  | { action: 'attest'; id: string; note?: string | null }
  | { action: 'reassign'; id: string; assigneeUserId: string }
  | { action: 'reroute'; id: string; routeTo: string }
  | { action: 'due-date'; id: string; dueAt: string }

export type BackstopActionKind = BackstopAction['action']

/** The body each intervention sends — nothing more than its own field. */
function bodyOf(request: BackstopAction): Record<string, unknown> {
  switch (request.action) {
    case 'force-close':
    case 'refuse':
      return { reason: request.reason }
    case 'forward':
    case 'attest':
      return { note: request.note ?? null }
    case 'reassign':
      return { assigneeUserId: request.assigneeUserId }
    case 'reroute':
      return { routeTo: request.routeTo }
    case 'due-date':
      return { dueAt: request.dueAt }
  }
}

/**
 * One intervention — the mutation's single entry point. Answers the request as it now stands; a refusal is an ApiError
 * whose ProblemDetails `code` is the store's RequestResult name (Stale, IllegalTransition, NotPermitted, …).
 */
export function actOnRequest(request: BackstopAction): Promise<BackstopRequest> {
  return fetchApi<BackstopRequest>(`${BASE}/${encodeURIComponent(request.id)}/${request.action}`, {
    method: 'POST',
    body: JSON.stringify(bodyOf(request)),
    auth: 'bearer-only',
  })
}
